import React, { useState } from "react";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface TimeOption {
  value: string; // 24-hour format "HH:mm"
  label: string; // 12-hour format "h:mm AM/PM"
}

interface MTimePickerProps {
  id?: string;
  className?: string;
  value: string | null; // Expected format: "HH:mm" (24-hour)
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  variant?: "standard" | "outlined" | "filled";
  placeholder?: string;
  required?: boolean;
}

/**
 * Generates time options in 15-minute intervals from 12:00 AM to 11:45 PM
 * Returns 96 options total
 */
const generateTimeOptions = (): TimeOption[] => {
  const options: TimeOption[] = [];
  
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      // 24-hour format value (for storage/comparison)
      const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      
      // 12-hour format label (for display)
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const amPm = hour < 12 ? "AM" : "PM";
      const label = `${hour12}:${minute.toString().padStart(2, "0")} ${amPm}`;
      
      options.push({ value, label });
    }
  }
  
  return options;
};

// Pre-generate options since they never change
const TIME_OPTIONS = generateTimeOptions();

/**
 * Converts a 24-hour time string to 12-hour display format
 */
const formatTimeForDisplay = (value: string | null): string => {
  if (!value) return "";
  
  // Check if it's already in 12-hour format (contains AM/PM)
  if (value.includes("AM") || value.includes("PM")) {
    return value;
  }
  
  // Try to find exact match in options
  const option = TIME_OPTIONS.find(opt => opt.value === value);
  if (option) {
    return option.label;
  }
  
  // Parse and convert manually for non-standard times
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (match) {
    const hour = parseInt(match[1], 10);
    const minute = match[2];
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const amPm = hour < 12 ? "AM" : "PM";
    return `${hour12}:${minute} ${amPm}`;
  }
  
  return value;
};

/**
 * Attempts to parse user input into 24-hour "HH:mm" format
 */
const parseTimeInput = (input: string): string => {
  if (!input) return "";
  
  const trimmed = input.trim().toUpperCase();
  
  // Check if it matches a known option label
  const optionByLabel = TIME_OPTIONS.find(opt => opt.label.toUpperCase() === trimmed);
  if (optionByLabel) {
    return optionByLabel.value;
  }
  
  // Try to parse "h:mm AM/PM" or "hh:mm AM/PM"
  const amPmMatch = trimmed.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (amPmMatch) {
    let hour = parseInt(amPmMatch[1], 10);
    const minute = amPmMatch[2];
    const period = amPmMatch[3];
    
    if (period === "PM" && hour !== 12) {
      hour += 12;
    } else if (period === "AM" && hour === 12) {
      hour = 0;
    }
    
    return `${hour.toString().padStart(2, "0")}:${minute}`;
  }
  
  // Try to parse "HH:mm" (24-hour format)
  const militaryMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (militaryMatch) {
    const hour = parseInt(militaryMatch[1], 10);
    const minute = militaryMatch[2];
    if (hour >= 0 && hour <= 23) {
      return `${hour.toString().padStart(2, "0")}:${minute}`;
    }
  }
  
  // Return as-is if we can't parse it
  return input;
};

/**
 * MTimePicker - A time picker with manual input and dropdown menu
 * 
 * Allows typing times directly or selecting from a dropdown with 15-minute intervals.
 * Values are stored in 24-hour format ("HH:mm") for consistency with existing code.
 * Display shows 12-hour format with AM/PM for user friendliness.
 */
const MTimePicker: React.FC<MTimePickerProps> = ({
  id,
  className,
  value,
  onChange,
  onBlur,
  label,
  disabled = false,
  error = false,
  helperText,
  fullWidth = true,
  variant = "standard",
  placeholder,
  required = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [displayValue, setDisplayValue] = useState<string>(() => formatTimeForDisplay(value));
  
  // Update display value when external value changes
  React.useEffect(() => {
    setDisplayValue(formatTimeForDisplay(value));
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(event.target.value);
  };

  const handleInputBlur = () => {
    // Parse and normalize the input on blur
    const parsed = parseTimeInput(displayValue);
    const normalized = formatTimeForDisplay(parsed);
    setDisplayValue(normalized);
    onChange(parsed === "" ? null : parsed);
    onBlur?.();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuSelect = (optionValue: string) => {
    const option = TIME_OPTIONS.find(opt => opt.value === optionValue);
    setDisplayValue(option ? option.label : optionValue);
    onChange(optionValue);
    handleMenuClose();
  };

  return (
    <>
      <TextField
        id={id}
        className={className}
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        label={label}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        variant={variant}
        required={required}
        placeholder={placeholder}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleMenuOpen}
                  disabled={disabled}
                  edge="end"
                  size="small"
                  aria-label="open time picker"
                >
                  <AccessTimeIcon />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          style: {
            maxHeight: 300,
          },
        }}
      >
        {TIME_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            selected={value === option.value}
            onClick={() => handleMenuSelect(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default MTimePicker;
export { TIME_OPTIONS, generateTimeOptions };
export type { MTimePickerProps, TimeOption };
